import React, { useState } from 'react';
import type { DFStateV3, DFActionV3, PlayerV3, DiceV3 } from '../../../../games/dice-factory/dice-factory-v03';
import { SIDES_ORDER } from '../../../../games/dice-factory/dice-factory-v03';
import DiceRenderer from '../components/player/DiceRenderer';

interface Props {
  state: DFStateV3;
  myId: string;
  dispatch: (action: DFActionV3) => void;
}

// ── Counter ──────────────────────────────────────────────────────────────────

interface CounterProps {
  label: string;
  value: number;
  onAdjust?: (delta: number) => void;
  readonly?: boolean;
}

function Counter({ label, value, onAdjust, readonly }: CounterProps) {
  return (
    <div className="flex flex-col items-center gap-1 bg-gray-800 rounded-lg px-4 py-3 min-w-24">
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
      <div className="flex items-center gap-2">
        {!readonly && (
          <button
            onClick={() => onAdjust?.(-1)}
            disabled={value === 0}
            className="w-7 h-7 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-30 disabled:cursor-not-allowed font-bold text-lg leading-none flex items-center justify-center"
          >
            −
          </button>
        )}
        <span className="text-2xl font-bold w-10 text-center">{value}</span>
        {!readonly && (
          <button
            onClick={() => onAdjust?.(1)}
            className="w-7 h-7 rounded bg-gray-700 hover:bg-gray-600 font-bold text-lg leading-none flex items-center justify-center"
          >
            +
          </button>
        )}
      </div>
    </div>
  );
}

// ── Die card ─────────────────────────────────────────────────────────────────

interface DieCardProps {
  die: DiceV3;
  isMyBoard: boolean;
  onReroll: () => void;
  onPromote: () => void;
  onDemote: () => void;
  onToggleGrey: () => void;
  onRemove: () => void;
}

function DieCard({ die, isMyBoard, onReroll, onPromote, onDemote, onToggleGrey, onRemove }: DieCardProps) {
  const atMax = SIDES_ORDER.indexOf(die.sides) === SIDES_ORDER.length - 1;
  const atMin = SIDES_ORDER.indexOf(die.sides) === 0;

  return (
    <div className={`flex flex-col items-center gap-1 p-2 rounded-lg border transition-colors ${
      die.greyed ? 'border-gray-700 bg-gray-900' : 'border-gray-600 bg-gray-800'
    }`}>
      <DiceRenderer
        die={{ id: die.id, sides: die.sides, value: die.value }}
        size="md"
        dimmed={die.greyed}
      />
      {isMyBoard && (
        <div className="flex gap-0.5">
          <ActionBtn title="Reroll" onClick={onReroll} disabled={die.greyed}>⟳</ActionBtn>
          <ActionBtn title="Promote" onClick={onPromote} disabled={atMax}>↑</ActionBtn>
          <ActionBtn title="Demote" onClick={onDemote} disabled={atMin}>↓</ActionBtn>
          <ActionBtn title={die.greyed ? 'Un-grey' : 'Grey out'} onClick={onToggleGrey}>
            {die.greyed ? '●' : '○'}
          </ActionBtn>
          <ActionBtn title="Remove" onClick={onRemove} danger>×</ActionBtn>
        </div>
      )}
    </div>
  );
}

function ActionBtn({
  children, title, onClick, disabled, danger,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={disabled}
      className={`w-6 h-6 rounded text-xs font-bold flex items-center justify-center transition-colors
        disabled:opacity-30 disabled:cursor-not-allowed
        ${danger
          ? 'bg-red-900 hover:bg-red-700 text-red-200'
          : 'bg-gray-700 hover:bg-gray-600 text-gray-200'
        }`}
    >
      {children}
    </button>
  );
}

// ── Slot grid ─────────────────────────────────────────────────────────────────

interface SlotGridProps {
  player: PlayerV3;
  isMyBoard: boolean;
  onUpdateSlot: (slotNumber: number, label: string) => void;
}

function SlotGrid({ player, isMyBoard, onUpdateSlot }: SlotGridProps) {
  return (
    <div className="grid grid-cols-2 gap-2">
      {player.slots.map(slot => (
        <div key={slot.slotNumber} className="bg-gray-800 rounded-lg p-2 flex flex-col gap-1">
          <span className="text-xs font-bold text-gray-500">#{slot.slotNumber}</span>
          {isMyBoard ? (
            <input
              type="text"
              value={slot.label}
              onChange={e => onUpdateSlot(slot.slotNumber, e.target.value)}
              className="w-full bg-gray-700 text-sm text-white rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 border border-transparent focus:border-blue-500"
            />
          ) : (
            <span className="text-sm text-gray-200 px-2 py-1">{slot.label}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Player board ─────────────────────────────────────────────────────────────

interface PlayerBoardProps {
  player: PlayerV3;
  isMyBoard: boolean;
  dispatch: (action: DFActionV3) => void;
}

function PlayerBoard({ player, isMyBoard, dispatch }: PlayerBoardProps) {
  const pid = player.id;

  const rollAll = () => dispatch({ type: 'v3_roll_all', playerId: pid });
  const addDie = (sides: 4 | 6 | 8 | 10 | 12) => dispatch({ type: 'v3_add_die', playerId: pid, sides });
  const reroll = (dieId: string) => dispatch({ type: 'v3_reroll_die', playerId: pid, dieId });
  const promote = (dieId: string) => dispatch({ type: 'v3_promote_die', playerId: pid, dieId });
  const demote = (dieId: string) => dispatch({ type: 'v3_demote_die', playerId: pid, dieId });
  const toggleGrey = (dieId: string) => dispatch({ type: 'v3_toggle_grey', playerId: pid, dieId });
  const remove = (dieId: string) => dispatch({ type: 'v3_remove_die', playerId: pid, dieId });
  const updateSlot = (slotNumber: number, label: string) =>
    dispatch({ type: 'v3_update_slot', playerId: pid, slotNumber, label });
  const adjustVp = (delta: number) => dispatch({ type: 'v3_adjust_vp', playerId: pid, delta });
  const adjustPips = (delta: number) => dispatch({ type: 'v3_adjust_free_pips', playerId: pid, delta });

  return (
    <div className="flex gap-6">
      {/* Left column: slots */}
      <div className="w-64 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">Ability Slots</h3>
        <SlotGrid player={player} isMyBoard={isMyBoard} onUpdateSlot={updateSlot} />
      </div>

      {/* Right column: dice + counters */}
      <div className="flex-1 min-w-0">
        {/* Dice pool controls */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wide">Dice Pool</h3>
          {isMyBoard && (
            <>
              <button
                onClick={rollAll}
                disabled={player.dicePool.length === 0}
                className="px-3 py-1 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed text-sm font-semibold transition-colors"
              >
                Roll All
              </button>
              <div className="flex gap-1">
                {SIDES_ORDER.map(sides => (
                  <button
                    key={sides}
                    onClick={() => addDie(sides)}
                    title={`Add d${sides}`}
                    className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs font-semibold transition-colors"
                  >
                    +d{sides}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Dice pool */}
        <div className="flex flex-wrap gap-2 mb-6 min-h-20">
          {player.dicePool.length === 0 && (
            <span className="text-gray-600 text-sm italic self-center">
              {isMyBoard ? 'No dice — add some above' : 'Empty pool'}
            </span>
          )}
          {player.dicePool.map(die => (
            <DieCard
              key={die.id}
              die={die}
              isMyBoard={isMyBoard}
              onReroll={() => reroll(die.id)}
              onPromote={() => promote(die.id)}
              onDemote={() => demote(die.id)}
              onToggleGrey={() => toggleGrey(die.id)}
              onRemove={() => remove(die.id)}
            />
          ))}
        </div>

        {/* Counters */}
        <div className="flex gap-4 flex-wrap">
          <Counter
            label="Victory Points"
            value={player.vp}
            onAdjust={isMyBoard ? adjustVp : undefined}
            readonly={!isMyBoard}
          />
          <Counter
            label="Free Pips"
            value={player.freePips}
            onAdjust={isMyBoard ? adjustPips : undefined}
            readonly={!isMyBoard}
          />
        </div>
      </div>
    </div>
  );
}

// ── Compact other-player row ──────────────────────────────────────────────────

function OtherPlayerRow({ player }: { player: PlayerV3 }) {
  return (
    <div className="bg-gray-800 rounded-lg p-3 flex items-center gap-4">
      <span className="text-sm font-semibold text-gray-300 w-24 truncate">{player.name}</span>
      <div className="flex flex-wrap gap-1 flex-1">
        {player.dicePool.map(die => (
          <DiceRenderer
            key={die.id}
            die={{ id: die.id, sides: die.sides, value: die.value }}
            size="sm"
            dimmed={die.greyed}
          />
        ))}
        {player.dicePool.length === 0 && <span className="text-gray-600 text-xs italic">no dice</span>}
      </div>
      <div className="flex gap-3 text-xs text-gray-400">
        <span>VP: <span className="text-white font-bold">{player.vp}</span></span>
        <span>Pips: <span className="text-white font-bold">{player.freePips}</span></span>
      </div>
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

export default function DiceFactoryV3({ state, myId, dispatch }: Props) {
  const [viewingId, setViewingId] = useState(myId);

  const viewedPlayer = state.players[viewingId] ?? state.players[state.playerOrder[0]];
  const isMyBoard = viewingId === myId;
  const others = state.playerOrder
    .filter(pid => pid !== viewingId)
    .map(pid => state.players[pid])
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 px-6 py-3 flex items-center justify-between">
        <span className="font-bold text-lg tracking-tight">Dice Factory <span className="text-gray-500 text-sm font-normal">v0.3</span></span>
        {/* Player tabs */}
        {state.playerOrder.length > 1 && (
          <div className="flex gap-1">
            {state.playerOrder.map(pid => {
              const p = state.players[pid];
              return (
                <button
                  key={pid}
                  onClick={() => setViewingId(pid)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    viewingId === pid
                      ? 'bg-blue-700 text-white'
                      : 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-white'
                  }`}
                >
                  {p?.name ?? pid}
                  {pid === myId && <span className="ml-1 text-xs text-blue-300">(you)</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-6 py-5">
        {/* Viewed player name */}
        <h2 className="text-xl font-bold mb-5">
          {viewedPlayer.name}
          {isMyBoard && <span className="ml-2 text-sm text-gray-500 font-normal">your board</span>}
        </h2>

        {/* Main board */}
        {viewedPlayer && (
          <PlayerBoard
            player={viewedPlayer}
            isMyBoard={isMyBoard}
            dispatch={dispatch}
          />
        )}

        {/* Other players (compact) */}
        {others.length > 0 && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Other Players</h3>
            <div className="flex flex-col gap-2">
              {others.map(p => <OtherPlayerRow key={p.id} player={p} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
